import { useEffect, useMemo, useState } from "react";

interface IUseAdminListPaginationConfig<T> {
  items: T[];
  itemsPerPage?: number;
}

interface IUseAdminListPaginationReturn<T> {
  paginatedItems: T[];
  selectedPageIndex: number;
  pageCount: number;
  handlePageChange: (index: number) => void;
  totalItemsCount: number;
  itemsPerPage: number;
}

export function useAdminListPagination<T>(
  config: IUseAdminListPaginationConfig<T>,
): IUseAdminListPaginationReturn<T> {
  const itemsPerPage = config.itemsPerPage ?? 10;
  const [selectedPageIndex, setSelectedPageIndex] = useState<number>(0);

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(config.items.length / itemsPerPage) - 1,
    );
    setSelectedPageIndex((prev) => (prev > maxPage ? maxPage : prev));
  }, [config.items.length, itemsPerPage]);

  const pageCount = useMemo(() => {
    return Math.ceil(config.items.length / itemsPerPage);
  }, [config.items.length, itemsPerPage]);

  const paginatedItems = useMemo(() => {
    const startIndex = selectedPageIndex * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return config.items.slice(startIndex, endIndex);
  }, [config.items, selectedPageIndex, itemsPerPage]);

  const handlePageChange = (index: number): void => {
    setSelectedPageIndex(index);
  };

  return {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount: config.items.length,
    itemsPerPage,
  };
}
